import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, ElementRef, HostBinding, Input, Optional, Self } from '@angular/core';
import { FormGroup, NgControl } from '@angular/forms';
import { MatFormField, MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';

export interface TimeRange {
  timeStart: string;
  timeEnd: string;
}
@Component({
  selector: 'time-range-picker',
  templateUrl: './time-range-picker.component.html',
  styleUrls: ['./time-range-picker.component.scss'],
  providers: [{provide: MatFormFieldControl, useExisting: TimeRangePickerComponent}]
})
export class TimeRangePickerComponent implements MatFormFieldControl<TimeRange> {
  static nextId = 0;
  stateChanges = new Subject<void>();
  focused: boolean = false;
  errorState: boolean = false;
  controlType?: string = 'time-range-picker';
  autofilled?: boolean;

  private _required : boolean = false;
  private _disabled: boolean = false;
  private _placeholder: string;

  @HostBinding() id = `time-range-picker-${TimeRangePickerComponent.nextId++}`;

  @Input()
  formGroup: FormGroup;
  @Input('aria-describedby') 
  userAriaDescribedBy: string;

  get value() : TimeRange | null {
    let fg = this.formGroup.value;
    if (fg.timeStart !== null && fg.timeEnd !== null){
      return { timeStart: fg.timeStart, timeEnd: fg.timeEnd};
    }
    return null;
  }
  set value(value: TimeRange | null) {
    value = value || { timeStart: null, timeEnd: null};
    this.formGroup.setValue({ hourStart: value.timeStart, timeStart: value.timeStart});
    this.stateChanges.next();
  }

  @Input()
  get placeholder() { 
    return this._placeholder;
  }
  set placeholder(value) {
    this._placeholder = value;
    this.stateChanges.next();
  }

  get empty() : boolean {
    return !this.formGroup.get('timeStart').value && !this.formGroup.get('timeEnd').value;
  }

  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  @Input()
  get required() : boolean {
    return this._required;
  }
  set required(req) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  @Input()
  get disabled(): boolean { 
    return this._disabled; 
  }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this._disabled ? this.formGroup.disable() : this.formGroup.enable();
    this.stateChanges.next();
  }

  constructor(private fm: FocusMonitor,
    private elRef: ElementRef<HTMLElement>,
    @Optional() public parentFormField: MatFormField,
    @Optional() @Self() public ngControl: NgControl)
  {
    fm.monitor(elRef, true).subscribe(origin => {
    this.focused = !!origin;
    this.stateChanges.next();
    });
  }
  
  setDescribedByIds(ids: string[]): void {
    const controlElement = this.elRef.nativeElement
      .querySelector('.time-range-picker-container');
    controlElement.setAttribute('aria-describedby', ids.join(' '));
  }

  onContainerClick(event: MouseEvent): void {
    if ((event.target as Element).tagName.toLowerCase() != 'input') {
      this.elRef.nativeElement.querySelector('input').focus();
    }
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
    this.fm.stopMonitoring(this.elRef);
  }
}